import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { QueryState } from '@/shared/ui/QueryState'
import { KakaoPointMap } from '@/shared/ui/KakaoPointMap'
import { api } from '@/shared/lib/api'
import { PUBLIC_STALE_TIME_MS } from '@/shared/lib/query'
import type { SolutionCard } from '@/shared/lib/apiTypes'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { makeLabeler, makeRegionLabeler, useCommonCodes, useRegions } from '@/shared/lib/codes'
import { formatDate } from '@/shared/lib/format'
import { CARD_CATEGORY_MAX } from '@/shared/constants/cards'
import { CarouselArrows } from '@/shared/ui/CarouselArrows'
import { useRowCarousel } from '@/shared/composables/useRowCarousel'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { SupportInfoPanel } from '@/public/features/solution/ui/SupportInfoPanel'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { LocationPinIcon } from '@/shared/ui/icons/LocationPinIcon'
import { ChevronRightIcon } from '@/shared/ui/icons/ChevronRightIcon'

const RECENT_SOLUTION_COUNT = 6

const REGISTERED_ORG_COUNT = 6

export function SolutionDetailPc({
  id,
  query,
}: {
  id: string | undefined
  query: UseQueryResult<SolutionCard>
}) {
  const { categoryLabel, targetLabel, isReady } = useCardLabels()
  const resourceLabel = makeLabeler(useCommonCodes('resource').data)
  const areaLabel = makeLabeler(useCommonCodes('service_area').data)
  const costLabel = makeLabeler(useCommonCodes('cost_type').data)
  const orgTypeLabel = makeLabeler(useCommonCodes('org_type').data)
  const certLabel = makeLabeler(useCommonCodes('cert_type').data)
  const regionLabel = makeRegionLabeler(useRegions().data)

  const [params] = useSearchParams()

  const recentRow = useRowCarousel()
  const orgRow = useRowCarousel()

  const recent = useQuery({
    queryKey: ['solution-cards', 'recent'],
    queryFn: () => api.solutionCards({ limit: 100 }),
    staleTime: PUBLIC_STALE_TIME_MS,
  })
  const orgs = useQuery({
    queryKey: ['orgs', 'registered'],
    queryFn: api.orgs,
    staleTime: PUBLIC_STALE_TIME_MS,
  })
  const fromId = params.get('from')
  const fromProblem = useQuery({
    queryKey: ['problem-card', fromId],
    queryFn: () => api.problemCard(fromId ?? ''),
    staleTime: PUBLIC_STALE_TIME_MS,
    enabled: fromId !== null,
    retry: false,
  })

  const orgId = query.data?.org.id
  const org = useQuery({
    queryKey: ['org', orgId],
    queryFn: () => api.org(orgId ?? ''),
    staleTime: PUBLIC_STALE_TIME_MS,
    enabled: orgId !== undefined,
  })

  const c = query.data

  return (
    <DefaultLayout tree="pc" className="[--body-w:1000px]">
      <QueryState isPending={query.isPending || !isReady} error={query.error}>
        {c === undefined ? null : (
          <div className="layout-body pt-8 pb-[60px]">
            <nav className="flex items-center gap-2 text-body-sm font-medium text-black-100">
              {fromProblem.data === undefined ? (
                <Link className="text-inherit no-underline" to="/solutions">
                  기업 해결 카드
                </Link>
              ) : (
                <>
                  <Link className="text-inherit no-underline" to="/problems">
                    시민문제
                  </Link>
                  <ChevronRightIcon size={16} />
                  <Link
                    className="text-inherit no-underline"
                    to={`/problems/${fromProblem.data.id}`}
                  >
                    {fromProblem.data.title}
                  </Link>
                </>
              )}
              <ChevronRightIcon size={16} />
              <span>해결카드 상세</span>
            </nav>

            <div className="mt-[46px] flex items-start gap-11">
              <div className="flex w-[640px] shrink-0 flex-col gap-[70px]">
                <section className="flex flex-col gap-7">
                  <Link
                    className="flex items-center gap-[12px] text-inherit no-underline"
                    to={`/orgs/${c.org.id}`}
                  >
                    <span className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-[99px] border-[1.25px] border-[rgb(0_0_0/5%)] bg-violet-100 text-black-100 [&>svg]:opacity-50">
                      <CityIcon size={24} />
                    </span>
                    <span className="flex flex-col gap-[3px]">
                      <span className="text-body-lg-reading font-medium text-black-500">
                        {c.org.name}
                      </span>
                      {c.org.certType !== null && (
                        <span className="text-body-sm text-black-100">
                          {certLabel(c.org.certType)}
                        </span>
                      )}
                    </span>
                  </Link>

                  <div className="flex flex-col gap-[10px]">
                    <h1 className="m-0 text-[20px] leading-[1.7] font-semibold tracking-[-0.1px] text-black-800">
                      {c.title}
                    </h1>
                    {c.summary.length > 0 && (
                      <p className="m-0 text-body-md-reading font-normal text-black-800">
                        {c.summary}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-[15px]">
                    <div className="flex min-w-px flex-1 flex-wrap items-center gap-2.5">
                      {c.categoryCodes.slice(0, CARD_CATEGORY_MAX).map((code) => (
                        <span
                          key={code}
                          className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-[#ebebeb] px-[15px] py-1 text-body-sm font-medium whitespace-nowrap text-[#464350]"
                        >
                          {categoryLabel(code)}
                        </span>
                      ))}
                    </div>
                    <span className="shrink-0 text-body-sm font-normal whitespace-nowrap text-black-100">
                      {formatDate(c.createdAt)}
                    </span>
                  </div>
                </section>

                <SupportInfoPanel
                  className="rounded-[24px] bg-[#f8f9fc] px-6 py-[18px]"
                  resourceChips={c.resourceCodes.map(resourceLabel)}
                  targetChips={c.targetCodes.map(targetLabel)}
                  region={
                    c.serviceDongCodes !== null && c.serviceDongCodes.length > 0
                      ? c.serviceDongCodes.map(regionLabel).join(' · ')
                      : c.serviceAreaCodes.map(areaLabel).join(' · ')
                  }
                  cost={costLabel(c.costType)}
                />
              </div>

              <aside className="flex w-[316px] shrink-0 flex-col gap-[30px] self-stretch overflow-clip rounded-[18px] bg-[#f8f9fc] px-[27px] py-[29px]">
                <div className="flex flex-col gap-9">
                  <div className="flex flex-col gap-9">
                    {c.org.certType !== null && (
                      <span className="inline-flex w-fit items-center justify-center rounded-lg bg-white-200 px-3 py-[3px] text-body-sm font-medium whitespace-nowrap text-black-600">
                        {certLabel(c.org.certType)}
                      </span>
                    )}
                    <div className="flex flex-col gap-[14px]">
                      <p className="m-0 text-headline-xs font-semibold text-black-800">
                        {c.org.name}
                      </p>
                    </div>
                  </div>

                  {org.data?.intro != null && org.data.intro.length > 0 && (
                    <section className="flex flex-col gap-[19px]">
                      <h2 className="m-0 text-caption font-medium text-black-100">기업·기관 소개</h2>
                      <p className="m-0 text-body-sm-reading whitespace-pre-wrap text-black-500">
                        {org.data.intro}
                      </p>
                    </section>
                  )}

                  {org.data?.location != null && (
                    <section className="flex flex-col gap-[19px]">
                      <h2 className="m-0 text-caption font-medium text-black-100">위치</h2>
                      <KakaoPointMap
                        lat={org.data.location.lat}
                        lng={org.data.location.lng}
                        className="h-[180px] w-full overflow-hidden rounded-[14px] border border-white-200 bg-white-50 [&_.map-canvas]:h-full [&_.map-canvas]:rounded-[inherit] [&_.map-canvas]:border-0"
                      />
                    </section>
                  )}

                  {c.accessCondition !== null && c.accessCondition.length > 0 && (
                    <section className="flex flex-col gap-[19px]">
                      <h2 className="m-0 text-caption font-medium text-black-100">이용 조건</h2>
                      <p className="m-0 text-body-sm-reading text-black-500">{c.accessCondition}</p>
                    </section>
                  )}
                </div>

                <Link
                  className="mt-auto flex h-12 items-center justify-center rounded-xl border border-white-400 bg-white-50 text-body-md font-medium text-black-500 no-underline"
                  to={fromId === null ? `/orgs/${c.org.id}` : `/orgs/${c.org.id}?from=${fromId}`}
                >
                  기업 자세히 보기
                </Link>
              </aside>
            </div>

            <section className="mt-[60px]">
              <div className="flex h-8 items-center">
                <h2 className="m-0 flex-1 text-body-lg font-semibold text-black-600">
                  최근 등록된 해결카드
                </h2>
                <Link
                  className="flex items-center gap-2 text-body-sm font-medium text-black-100 no-underline"
                  to="/solutions"
                >
                  더보기
                  <ChevronRightIcon size={12} />
                </Link>
              </div>
              <div className="relative mt-[18px]">
                <ul
                  ref={recentRow.ref}
                  className="m-0 flex list-none items-stretch gap-4 overflow-x-auto p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {(recent.data?.items ?? [])
                    .filter((item) => item.id !== id)
                    .slice(0, RECENT_SOLUTION_COUNT)
                    .map((item) => (
                      <li key={item.id} className="flex w-[322.67px] min-w-0 shrink-0">
                        <Link
                          className="flex w-full min-w-0 flex-col gap-5 rounded-2xl border border-white-200 bg-white-50 px-6 py-[22px] text-inherit no-underline shadow-[0_0_4px_-1px_rgb(0_0_0/8%),0_0_12px_-4px_rgb(0_0_0/16%)]"
                          to={`/solutions/${item.id}`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[99px] border-[1.25px] border-[rgb(0_0_0/5%)] bg-violet-100 text-black-100 [&>svg]:opacity-50">
                              <CityIcon size={20} />
                            </span>
                            <span className="truncate text-body-md-reading font-medium text-black-500">
                              {item.org.name}
                            </span>
                          </span>
                          <span className="flex flex-col gap-[14px]">
                            <span className="flex flex-wrap items-center gap-2">
                              {item.categoryCodes.slice(0, CARD_CATEGORY_MAX).map((code) => (
                                <span
                                  key={code}
                                  className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-violet-800 px-3 py-[5px] text-body-sm font-medium whitespace-nowrap text-white-50"
                                >
                                  {categoryLabel(code)}
                                </span>
                              ))}
                            </span>
                            <span className="text-body-lg-reading font-semibold text-black-800">
                              {item.title}
                            </span>
                            <span className="line-clamp-3 min-h-[5.1em] text-body-md-reading font-normal text-black-800">
                              {item.summary}
                            </span>
                          </span>
                          {item.accessCondition !== null && item.accessCondition.length > 0 && (
                            <span className="flex items-center gap-1 text-body-sm font-normal text-violet-600">
                              <InfoIcon size={20} />
                              {item.accessCondition}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                </ul>
                <CarouselArrows control={recentRow} label="최근 등록된 해결카드" top={127} />
              </div>
            </section>

            <section className="mt-[60px]">
              <h2 className="m-0 flex h-8 items-center text-body-lg font-semibold text-black-600">
                등록된 기업
              </h2>
              <div className="relative mt-[18px]">
                <ul
                  ref={orgRow.ref}
                  className="m-0 flex list-none items-stretch gap-7 overflow-x-auto p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {(orgs.data?.items ?? []).slice(0, REGISTERED_ORG_COUNT).map((org) => (
                    <li key={org.id} className="flex w-[143.33px] min-w-0 shrink-0">
                      <Link
                        className="flex w-full min-w-0 flex-col gap-3 text-inherit no-underline"
                        to={`/orgs/${org.id}`}
                      >
                        <span className="flex aspect-square w-full items-center justify-center rounded-xl bg-violet-100 text-black-100 [&>svg]:opacity-50">
                          <CityIcon size={40} />
                        </span>
                        <span className="flex flex-col gap-[2px] px-2">
                          <span className="truncate text-body-lg-reading font-medium text-black-500">
                            {org.name}
                          </span>
                          <span className="truncate text-body-sm text-black-100">
                            {orgTypeLabel(org.orgType)}
                          </span>
                          {org.baseRegionCode !== null && (
                            <span className="mt-2 flex items-center gap-1 text-body-sm text-black-100">
                              <LocationPinIcon size={16} />
                              <span className="truncate">{regionLabel(org.baseRegionCode)}</span>
                            </span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <CarouselArrows control={orgRow} label="등록된 기업" top={100} />
              </div>
            </section>
          </div>
        )}
      </QueryState>
    </DefaultLayout>
  )
}
